import { PaintBucket, Plus, Minus, ChevronDown } from 'lucide-react';
import { FormData } from '../../types';
import { colorPalettes } from '../../constants';
import { useState } from 'react';
import { ColorPicker } from '../ColorPicker';

interface ColorPreferencesProps {
  formData: FormData;
  errors: { [key: number]: boolean };
  duplicateColorError: boolean;
  handleCustomColorChange: (index: number, color: string) => void;
  handleColorPaletteSelect: (paletteName: string) => void;
  addColor: () => void;
  removeLastColor: () => void;
  setErrors: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
  showErrors: boolean;
}

export const ColorPreferences = ({
  formData,
  errors,
  duplicateColorError,
  handleCustomColorChange,
  handleColorPaletteSelect,
  addColor,
  removeLastColor,
  setErrors,
  showErrors
}: ColorPreferencesProps) => {
  const [visiblePalettes, setVisiblePalettes] = useState(10);
  const [activeColorPicker, setActiveColorPicker] = useState<number | null>(null);

  const loadMorePalettes = () => {
    setVisiblePalettes(prev => Math.min(prev + 10, colorPalettes.length));
  };

  return (
    <div className="space-y-8">
      <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-3 text-blue-900">
        <PaintBucket className="text-[#6366F1]" />
        Color Preferences
      </h2>
      <div className="space-y-8">
        <div>
          <h3 className="text-base sm:text-lg font-medium mb-4 text-gray-800">
            Do you have brand colors? <span className="text-red-500">*</span>
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex flex-wrap sm:flex-nowrap w-full sm:w-auto gap-2">
                {formData.customColors.slice(0, formData.numCustomColors).map((color, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveColorPicker(index)}
                    style={{ backgroundColor: color }}
                    className="h-16 w-full sm:w-32 flex items-center justify-center relative group first:rounded-lg sm:first:rounded-l-lg sm:first:rounded-r-none last:rounded-lg sm:last:rounded-r-lg sm:last:rounded-l-none cursor-pointer"
                  >
                    <div className="w-full px-2">
                      <div
                        className={`w-full bg-black/10 backdrop-blur-sm text-center font-mono text-sm py-1 rounded
                          ${color === '#FFFFFF' || color === '#FFF' || color === '#ffffff' || color === '#fff'
                            ? 'text-black'
                            : 'text-white'
                          } ${errors[index] && showErrors ? "ring-2 ring-red-500" : ""}`}
                      >
                        {color.toUpperCase()}
                      </div>
                    </div>
                    {activeColorPicker === index && (
                      <ColorPicker
                        color={color}
                        onChange={(newColor) => handleCustomColorChange(index, newColor)}
                        onClose={() => setActiveColorPicker(null)}
                      />
                    )}
                    {errors[index] && showErrors && (
                      <p className="absolute -bottom-6 left-0 text-red-500 text-xs whitespace-nowrap">
                        Invalid Hex
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                {formData.numCustomColors < 4 && (
                  <button
                    onClick={addColor}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:border-[#6366F1] hover:bg-[#F5F3FF] transition-colors"
                  >
                    <Plus size={16} />
                    <span className="hidden sm:inline">Add Color</span>
                  </button>
                )}
                {formData.numCustomColors > 2 && (
                  <button
                    onClick={removeLastColor}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:border-[#6366F1] hover:bg-[#F5F3FF] transition-colors"
                  >
                    <Minus size={16} />
                    <span className="hidden sm:inline">Remove Color</span>
                  </button>
                )}
              </div>
            </div>
            {duplicateColorError && showErrors && (
              <p className="text-red-500 text-sm">Colors must be unique to proceed</p>
            )}
          </div>
        </div>
        
        <div>
          <div className="space-y-2 mb-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-800">
              Or choose a color palette <span className="text-red-500">*</span>
            </h3>
            {showErrors && !formData.usingCustomColors && formData.colorPreferences.length === 0 && (
              <p className="mt-1 text-sm text-red-600">Please select a color palette or specify custom colors</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {colorPalettes.slice(0, visiblePalettes).map((palette) => (
              <div key={palette.name} className="space-y-2">
                <div
                  onClick={() => handleColorPaletteSelect(palette.name)}
                  className={`h-16 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                    formData.colorPreferences.includes(palette.name)
                      ? 'ring-2 ring-[#6366F1]'
                      : 'ring-1 ring-gray-200 hover:ring-[#6366F1]'
                  }`}
                >
                  <div className="flex h-full">
                    {palette.colors.map((color) => (
                      <div
                        key={color}
                        className="flex-1 h-full"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <p className={`text-sm font-medium transition-colors duration-300 ${
                  formData.colorPreferences.includes(palette.name)
                    ? 'text-[#6366F1]'
                    : 'text-gray-700'
                }`}>
                  {palette.name}
                </p>
              </div>
            ))}
          </div>
          {visiblePalettes < colorPalettes.length && (
            <div className="mt-8 text-center">
              <button
                onClick={loadMorePalettes}
                className="flex items-center gap-2 px-6 py-3 mx-auto text-[#6366F1] font-medium hover:bg-[#F5F3FF] rounded-full transition-colors"
              >
                View More
                <ChevronDown size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};